// =============================================================================
// PasswordFields — shared new-password + confirm inputs with strength hint
// (password auth v1, 2026-09)
// =============================================================================
// Client-side checks are length + match only; the server is authoritative
// for the common-password blocklist and identity-containment checks (its
// WEAK_PASSWORD reason is surfaced by the parent via `error`).
// =============================================================================

import { useState } from 'react';
import { Eye, EyeOff, Check } from 'lucide-react';

const MIN_LENGTH = 12;

interface PasswordFieldsProps {
  newPassword: string;
  confirmPassword: string;
  onNewPasswordChange: (v: string) => void;
  onConfirmPasswordChange: (v: string) => void;
  newPasswordLabel?: string;
  autoCompleteNew?: string;
  error?: string;
}

/** Length-tier strength hint: no composition rules by policy (NIST). */
function strengthTier(len: number): { label: string; color: string; width: string } {
  if (len === 0) return { label: '', color: 'bg-gray-200', width: 'w-0' };
  if (len < MIN_LENGTH) return { label: `At least ${MIN_LENGTH} characters`, color: 'bg-red-400', width: 'w-1/4' };
  if (len < 16) return { label: 'Good', color: 'bg-yellow-400', width: 'w-2/4' };
  if (len < 20) return { label: 'Strong', color: 'bg-[#17DDAB]', width: 'w-3/4' };
  return { label: 'Very strong', color: 'bg-[#17DDAB]', width: 'w-full' };
}

export function PasswordFields({
  newPassword,
  confirmPassword,
  onNewPasswordChange,
  onConfirmPasswordChange,
  newPasswordLabel = 'Create password',
  autoCompleteNew = 'new-password',
  error,
}: PasswordFieldsProps) {
  const [showPassword, setShowPassword] = useState(false);
  const tier = strengthTier(newPassword.length);
  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;

  const inputClass = (hasError: boolean) =>
    `mt-1 block w-full px-3 py-2 pr-10 border ${
      hasError ? 'border-red-500' : 'border-gray-300'
    } rounded-md shadow-sm focus:outline-none focus:ring-[#009FE4] focus:border-[#009FE4]`;

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="new-password" className="block text-sm font-medium text-gray-700">
          {newPasswordLabel}
        </label>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            id="new-password"
            value={newPassword}
            onChange={(e) => onNewPasswordChange(e.target.value)}
            placeholder={`At least ${MIN_LENGTH} characters`}
            autoComplete={autoCompleteNew}
            maxLength={128}
            className={inputClass(!!error)}
          />
          <button
            type="button"
            tabIndex={-1}
            onClick={() => setShowPassword((s) => !s)}
            className="absolute inset-y-0 right-0 mt-1 flex items-center pr-3 text-gray-400 hover:text-gray-600"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        {/* Strength bar (length-based tiers; longer is stronger) */}
        <div className="mt-2 h-1 w-full rounded bg-gray-100">
          <div className={`h-1 rounded transition-all ${tier.color} ${tier.width}`} />
        </div>
        {tier.label && (
          <p className={`mt-1 text-xs ${newPassword.length < MIN_LENGTH ? 'text-red-500' : 'text-gray-500'}`}>
            {newPassword.length >= MIN_LENGTH && <Check className="inline w-3 h-3 mr-0.5 -mt-0.5" />}
            {tier.label}
          </p>
        )}
      </div>

      <div>
        <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700">
          Confirm password
        </label>
        <input
          type={showPassword ? 'text' : 'password'}
          id="confirm-password"
          value={confirmPassword}
          onChange={(e) => onConfirmPasswordChange(e.target.value)}
          placeholder="Re-enter your password"
          autoComplete={autoCompleteNew}
          maxLength={128}
          className={inputClass(mismatch)}
        />
        {mismatch && <p className="mt-1 text-xs text-red-500">Passwords do not match</p>}
      </div>
    </div>
  );
}
